import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, Loader2, ChevronLeft, ChevronRight, Users, Eye, Heart, Music, Crown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SORT_OPTIONS = [
  { value: 'popular', label: 'Most popular' },
  { value: 'newest', label: 'Newest' },
  { value: 'alphabetical', label: 'A → Z' },
];

const formatNum = (n) => {
  if (n == null) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
};

const resolveAvatar = (a) => {
  if (!a) return null;
  if (a.startsWith('http')) return a;
  return `${API}/api/files/${a}`;
};

// Single artist card. Click → /creator/:username (public profile).
const ArtistCard = ({ artist }) => {
  const target = `/creator/${artist.username || artist.id}`;
  const avatarUrl = resolveAvatar(artist.avatar);
  return (
    <Link
      to={target}
      className="group relative flex flex-col bg-zinc-900/60 border border-zinc-800 hover:border-fuchsia-500/40 rounded-2xl p-5 transition-all hover:-translate-y-0.5"
      data-testid={`artist-card-${artist.username}`}
    >
      <div className="flex items-start gap-3 mb-3">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            className="w-14 h-14 rounded-full object-cover ring-2 ring-zinc-800 group-hover:ring-fuchsia-500/40 transition"
            loading="lazy"
          />
        ) : (
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-fuchsia-500 to-cyan-500 flex items-center justify-center text-white text-xl font-semibold ring-2 ring-zinc-800">
            {(artist.display_name || artist.username || '?').charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="font-semibold text-white truncate" title={artist.display_name}>
              {artist.display_name}
            </p>
            {artist.founding_number && artist.founding_number <= 100 && (
              <Crown
                className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0"
                title={`Founding Creator #${artist.founding_number}`}
              />
            )}
          </div>
          <p className="text-xs text-zinc-500 truncate">@{artist.username}</p>
        </div>
      </div>

      {artist.bio && (
        <p className="text-sm text-zinc-400 line-clamp-2 mb-3 leading-snug">{artist.bio}</p>
      )}

      <div className="mt-auto flex items-center gap-3 text-xs text-zinc-500 pt-3 border-t border-zinc-800/60">
        <span className="flex items-center gap-1" title="Videos">
          <Music className="w-3.5 h-3.5" />
          {artist.video_count}
        </span>
        <span className="flex items-center gap-1" title="Total views">
          <Eye className="w-3.5 h-3.5" />
          {formatNum(artist.total_views)}
        </span>
        <span className="flex items-center gap-1" title="Total likes">
          <Heart className="w-3.5 h-3.5" />
          {formatNum(artist.total_likes)}
        </span>
      </div>
    </Link>
  );
};

export const ArtistsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sort = searchParams.get('sort') || 'popular';
  const search = searchParams.get('q') || '';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10));

  const [searchInput, setSearchInput] = useState(search);
  const [data, setData] = useState({ artists: [], total: 0, total_pages: 0 });
  const [loading, setLoading] = useState(true);

  const fetchArtists = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort, page: String(page), per_page: '24' });
      if (search) params.set('search', search);
      const res = await axios.get(`${API}/api/artists?${params.toString()}`);
      setData(res.data || { artists: [], total: 0, total_pages: 0 });
    } catch (err) {
      console.error('Failed to load artist directory:', err);
      setData({ artists: [], total: 0, total_pages: 0 });
    } finally {
      setLoading(false);
    }
  }, [sort, search, page]);

  useEffect(() => {
    fetchArtists();
  }, [fetchArtists]);

  // Debounced search — wait 350ms after the user stops typing to update the URL
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput === search) return;
      const next = new URLSearchParams(searchParams);
      if (searchInput) next.set('q', searchInput);
      else next.delete('q');
      next.set('page', '1'); // reset to first page on new search
      setSearchParams(next, { replace: true });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const handleSortChange = (newSort) => {
    const next = new URLSearchParams(searchParams);
    next.set('sort', newSort);
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  const goToPage = (p) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(p));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="artists-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500/20 to-cyan-500/20 border border-white/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-fuchsia-300" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Artist Directory</h1>
          </div>
          <p className="text-sm sm:text-base text-zinc-400 max-w-2xl">
            Every creator publishing AI music videos on AUDIVUE. Sort by popularity, browse the newest signups, or search by name.
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search artists by name or @username…"
              className="w-full pl-10 pr-3 py-2.5 rounded-full bg-zinc-900 border border-zinc-800 focus:border-fuchsia-500/50 outline-none text-sm text-white placeholder:text-zinc-500"
              data-testid="artists-search-input"
            />
          </div>
          <div className="flex gap-1.5 bg-zinc-900 border border-zinc-800 rounded-full p-1">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => handleSortChange(opt.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  sort === opt.value
                    ? 'bg-white text-black'
                    : 'text-zinc-400 hover:text-white'
                }`}
                data-testid={`artists-sort-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Result count */}
        {!loading && (
          <p className="text-xs text-zinc-500 mb-4">
            {data.total === 0
              ? (search ? `No artists match "${search}".` : 'No artists yet.')
              : `${data.total} artist${data.total !== 1 ? 's' : ''}${search ? ` matching "${search}"` : ''}`}
          </p>
        )}

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-7 h-7 animate-spin text-fuchsia-400" />
          </div>
        ) : data.artists.length === 0 ? (
          <div className="text-center py-16 text-zinc-500">
            <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">
              {search ? 'Try a different search.' : 'Be the first to publish — your profile will appear here.'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {data.artists.map((a) => (
                <ArtistCard key={a.id} artist={a} />
              ))}
            </div>

            {/* Pagination */}
            {data.total_pages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-10" data-testid="artists-pagination">
                <button
                  disabled={page <= 1}
                  onClick={() => goToPage(page - 1)}
                  className="p-2 rounded-full bg-zinc-900 border border-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-zinc-800"
                  data-testid="artists-prev-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-zinc-400 min-w-[80px] text-center">
                  Page {page} of {data.total_pages}
                </span>
                <button
                  disabled={page >= data.total_pages}
                  onClick={() => goToPage(page + 1)}
                  className="p-2 rounded-full bg-zinc-900 border border-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-zinc-800"
                  data-testid="artists-next-page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ArtistsPage;
